import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RefreshCw, ExternalLink } from "lucide-react";

/**
 * AdminEntryTariffQueue — human-in-the-loop sign-off for AI-proposed
 * entry-gate rows produced by the `wua-entry-tariff-extract` daemon.
 *
 * Each pending row shows:
 *   • the AI's proposed {published_ucas_tariff, entry_gate,
 *     a_level_equivalent, confidence, evidence_snippet}
 *   • a link to the source page it was extracted from
 *   • the current live degrees.admissions snapshot for that uni
 *     (so an admin can see "we currently show `null` tariff /
 *     `grades` gate — proposal wants to bump to 96 / grades")
 *   • Approve / Reject buttons
 *
 * Approving fires POST /admin/entry-tariff/{uni}/approve — the API
 * writes the tariff+gate to every degree at that uni and marks the
 * pending row `applied`. Rejecting marks it `rejected` so the daemon
 * doesn't re-propose it.
 */

const CONF_TINT = {
  high: "bg-emerald-100 text-emerald-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-rose-100 text-rose-800",
};

function conf_tint(c) {
  if (c == null) return "bg-stone-100 text-stone-700";
  if (typeof c === "number") {
    if (c >= 0.8) return CONF_TINT.high;
    if (c >= 0.5) return CONF_TINT.medium;
    return CONF_TINT.low;
  }
  return CONF_TINT[c] || "bg-stone-100 text-stone-700";
}

const GATE_TINT = {
  grades: "bg-sky-100 text-sky-800",
  audition: "bg-fuchsia-100 text-fuchsia-800",
  portfolio: "bg-indigo-100 text-indigo-800",
};

export default function AdminEntryTariffQueue() {
  const [state, setState] = useState({ rows: [], counts: {}, loading: true });
  const [busy, setBusy] = useState(null);   // university_id currently mid-action
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/entry-tariff/pending");
      setState({ rows: data.rows, counts: data.counts, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
      setState({ rows: [], counts: {}, loading: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (uniId) => {
    setBusy(uniId);
    try {
      const { data } = await api.post(`/admin/entry-tariff/${uniId}/approve`);
      toast.success(`Applied to ${data.degrees_updated} degrees`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (uniId) => {
    const reason = window.prompt("Optional rejection reason:", "") || null;
    setBusy(uniId);
    try {
      await api.post(`/admin/entry-tariff/${uniId}/reject`,
        null, { params: reason ? { reason } : {} });
      toast.success("Rejected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(null);
    }
  };

  const rows = state.rows.filter(
    (r) => filter === "all" || (r.status || "pending") === filter
  );

  return (
    <div
      data-testid="entry-tariff-queue-page"
      className="max-w-6xl mx-auto px-4 py-8"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-stone-900">
            Entry-Tariff Review Queue
          </h1>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            AI-proposed entry-gate rows from{" "}
            <code>wua-entry-tariff-extract</code>. Each approval writes{" "}
            <code>admissions.published_ucas_tariff</code> and{" "}
            <code>admissions.entry_gate</code> to every degree at that
            university and (if the gate is audition/portfolio) invalidates
            the <code>llm_cache</code>.
          </p>
        </div>
        <button
          data-testid="refresh-btn"
          onClick={load}
          className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        {[
          ["pending", `Pending (${state.counts.pending || 0})`],
          ["applied", `Applied (${state.counts.applied || 0})`],
          ["rejected", `Rejected (${state.counts.rejected || 0})`],
          ["all", "All"],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              filter === k
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {state.loading ? (
        <div className="text-stone-500 text-sm flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading proposals…
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="empty-state"
          className="rounded-xl border border-dashed border-stone-200 p-10 text-center text-stone-500"
        >
          No {filter === "all" ? "" : filter} proposals in the queue.
          <br />
          <span className="text-xs">
            Run <code>python -m scripts.extract_entry_tariff_ai_daemon
            --commit</code> to generate new ones.
          </span>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <ProposalCard
              key={r.university_id + (r.status || "")}
              row={r}
              onApprove={() => approve(r.university_id)}
              onReject={() => reject(r.university_id)}
              busy={busy === r.university_id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProposalCard({ row, onApprove, onReject, busy }) {
  const status = row.status || "pending";
  const isPending = status === "pending";

  return (
    <div
      data-testid={`proposal-${row.university_id}`}
      className={`border rounded-xl p-5 bg-white shadow-sm transition ${
        isPending ? "border-stone-200" : "border-stone-100 bg-stone-50/50"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-stone-900">
              {row.university_name}
            </h3>
            <span className="text-xs text-stone-400">{row.university_id}</span>
            {!isPending && (
              <span
                className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                  status === "applied"
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-rose-100 text-rose-800"
                }`}
              >
                {status.toUpperCase()}
              </span>
            )}
          </div>
          <a
            data-testid={`source-link-${row.university_id}`}
            href={row.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-stone-500 hover:text-stone-900 hover:underline inline-flex items-center gap-1"
          >
            {row.source_url}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        <span
          className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${conf_tint(
            row.confidence
          )}`}
        >
          conf: {typeof row.confidence === "number"
            ? row.confidence.toFixed(2)
            : row.confidence}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-stone-100 p-3">
          <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-1">
            Current (live)
          </div>
          <div>
            Tariff:{" "}
            <span className="font-medium text-stone-900">
              {row.live?.current_tariff ?? "—"}
            </span>{" "}
            · Gate:{" "}
            <span className="font-medium text-stone-900">
              {row.live?.current_gate ?? "—"}
            </span>
          </div>
          <div className="text-xs text-stone-500 mt-1">
            {row.live?.degrees_count ?? 0} degree(s) at this uni
          </div>
        </div>

        <div className="rounded-lg border border-stone-200 p-3 bg-amber-50/30">
          <div className="text-[11px] uppercase tracking-wide text-amber-800 mb-1">
            Proposed
          </div>
          <div>
            Tariff:{" "}
            <span className="font-medium text-stone-900">
              {row.published_ucas_tariff}
            </span>{" "}
            {row.a_level_equivalent && (
              <span className="text-xs text-stone-500">
                (~ {row.a_level_equivalent})
              </span>
            )}{" "}
            · Gate:{" "}
            <span
              className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                GATE_TINT[row.entry_gate] || "bg-stone-100 text-stone-700"
              }`}
            >
              {row.entry_gate}
            </span>
          </div>
          <div className="text-xs text-stone-500 mt-1">
            via {row.fetched_via || "html"}
          </div>
        </div>
      </div>

      {row.evidence_snippet && (
        <blockquote
          data-testid={`evidence-${row.university_id}`}
          className="mt-3 text-xs text-stone-600 italic border-l-2 border-stone-200 pl-3"
        >
          "{row.evidence_snippet}"
        </blockquote>
      )}

      {isPending && (
        <div className="mt-4 flex items-center gap-2">
          <button
            data-testid={`approve-btn-${row.university_id}`}
            disabled={busy}
            onClick={onApprove}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Approve
          </button>
          <button
            data-testid={`reject-btn-${row.university_id}`}
            disabled={busy}
            onClick={onReject}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white hover:bg-stone-50 text-stone-700 border border-stone-200 text-sm font-medium disabled:opacity-50 transition"
          >
            <XCircle className="h-4 w-4" />
            Reject
          </button>
        </div>
      )}

      {status === "applied" && row.applied_at && (
        <div className="mt-3 text-[11px] text-emerald-800">
          Applied {new Date(row.applied_at).toLocaleString()} by{" "}
          {row.applied_by || "admin"} · tariff→
          {row.applied_tariff}, gate→{row.applied_gate}
        </div>
      )}
      {status === "rejected" && row.rejected_at && (
        <div className="mt-3 text-[11px] text-rose-800">
          Rejected {new Date(row.rejected_at).toLocaleString()} by{" "}
          {row.rejected_by || "admin"}
          {row.rejected_reason ? ` — ${row.rejected_reason}` : ""}
        </div>
      )}
    </div>
  );
}
